import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllDoctors, getDoctorById, getDoctorsByDepartmentIdMultiple } from "../../services/doctorService";
import { getAllDepartment } from "../../services/departmentService";
import { getPrescriptionById } from "../../services/prescriptionService";
import { validatePrescriptionForLabBooking } from "../patientDashboard/appointmentPatientUtils";
import { filterDepartmentsForOpdWorkflow } from "../../utils/subscriptionScopeUtils";

const BLUE = "#0B78D0";
const BLUE_DARK = "#0866B3";
const BLUE_LIGHT = "#EAF4FC";
const TEXT_GREY = "#6B7280";
const BORDER = "#E5E9EF";

const SEARCH_FIELD_SX = {
  flex: "1 1 180px",
  minWidth: { xs: "100%", sm: 160 },
  "& .MuiOutlinedInput-root": {
    height: 40,
    backgroundColor: "#fff",
    fontSize: "0.875rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
  },
};

const collectDoctorDepartmentIds = (doctor) => {
  const ids = new Set();
  const add = (value) => {
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }
    if (typeof value === "object") {
      add(
        value.id ??
          value.departmentId ??
          value.department_id ??
          value.departmentID
      );
      return;
    }
    String(value)
      .split(/[,|]/)
      .forEach((part) => {
        const trimmed = part.trim();
        if (trimmed) ids.add(trimmed);
      });
  };

  add(doctor?.departmentId);
  add(doctor?.department_id);
  add(doctor?.departmentID);
  add(doctor?.departmentIds);
  add(doctor?.department_ids);
  add(doctor?.departments);
  return [...ids];
};

const mergeDoctorsById = (doctorList = []) => {
  const map = new Map();
  doctorList.forEach((doctor) => {
    const key =
      doctor?.id ??
      doctor?.userId ??
      doctor?.user_id ??
      `${doctor?.name}-${doctor?.mobile}`;
    const incomingIds = collectDoctorDepartmentIds(doctor);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        ...doctor,
        departmentIds: incomingIds,
      });
      return;
    }
    map.set(key, {
      ...existing,
      ...doctor,
      departmentIds: [
        ...new Set([...collectDoctorDepartmentIds(existing), ...incomingIds]),
      ],
    });
  });
  return Array.from(map.values()).filter((doctor) => doctor?.is_active !== 0);
};

const getDoctorId = (doctor) => doctor?.id ?? doctor?.userId ?? doctor?.user_id;

const getDoctorName = (doctor) =>
  doctor?.name ?? doctor?.fullName ?? doctor?.full_name ?? "Doctor";

const getDepartmentName = (department) =>
  department?.name ?? department?.departmentName ?? department?.department_name ?? "";

const PatientDoctorSearchPanel = ({
  appointmentType = "in-person",
  t = (key) => key,
  initialDepartmentId = "",
  requirePrescription = false,
  patientId = null,
}) => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [departmentDoctors, setDepartmentDoctors] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(initialDepartmentId || "");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [doctorSearch, setDoctorSearch] = useState("");
  const [prescriptionId, setPrescriptionId] = useState("");
  const [prescribedTestOptions, setPrescribedTestOptions] = useState([]);
  const [selectedPrescribedTests, setSelectedPrescribedTests] = useState([]);
  const [lockedDoctor, setLockedDoctor] = useState(null);
  const [verifyingPrescription, setVerifyingPrescription] = useState(false);
  const prescriptionLookupRef = useRef(0);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  useEffect(() => {
    setSelectedDepartment(initialDepartmentId || "");
  }, [initialDepartmentId]);

  useEffect(() => {
    let isMounted = true;
    const loadDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const data = await getAllDepartment();
        if (!isMounted) return;
        setDepartmentList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error loading departments:", error);
        if (isMounted) setDepartmentList([]);
      } finally {
        if (isMounted) setLoadingDepartments(false);
      }
    };
    loadDepartments();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadDoctors = async () => {
      try {
        setLoadingDoctors(true);
        const data = await getAllDoctors({ forceRefresh: false });
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        setDoctors(mergeDoctorsById(list));
      } catch (error) {
        console.error("Error loading doctors:", error);
        if (isMounted) setDoctors([]);
      } finally {
        if (isMounted) setLoadingDoctors(false);
      }
    };
    loadDoctors();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadDoctorsForDepartment = async () => {
      if (!selectedDepartment) {
        setDepartmentDoctors([]);
        return;
      }
      try {
        const list = await getDoctorsByDepartmentIdMultiple(selectedDepartment);
        if (!isMounted) return;
        const mapped = (Array.isArray(list) ? list : []).map((doctor) => ({
          ...doctor,
          departmentIds: [
            ...new Set([...collectDoctorDepartmentIds(doctor), String(selectedDepartment)]),
          ],
        }));
        setDepartmentDoctors(mergeDoctorsById(mapped));
      } catch (error) {
        console.warn("Unable to load doctors for selected department:", error);
        if (isMounted) setDepartmentDoctors([]);
      }
    };
    loadDoctorsForDepartment();
    return () => {
      isMounted = false;
    };
  }, [selectedDepartment]);

  const allDepartments = useMemo(() => {
    const opdDepartments = filterDepartmentsForOpdWorkflow(departmentList, {
      keepIds: [selectedDepartment],
    });
    return [...opdDepartments].sort((a, b) =>
      String(getDepartmentName(a)).localeCompare(String(getDepartmentName(b)))
    );
  }, [departmentList, selectedDepartment]);

  const doctorsInHospital = useMemo(() => {
    if (selectedDepartment && departmentDoctors.length > 0) {
      return departmentDoctors;
    }
    return mergeDoctorsById([...doctors, ...(selectedDepartment ? departmentDoctors : [])]);
  }, [doctors, departmentDoctors, selectedDepartment]);

  const searchValue = doctorSearch.trim().toLowerCase();

  const getDoctorDepartmentNames = (doctor) => {
    const names = new Set();
    const addName = (value) => {
      if (value == null || value === "") return;
      if (Array.isArray(value)) {
        value.forEach(addName);
        return;
      }
      if (typeof value === "object") {
        addName(
          value.name ??
            value.departmentName ??
            value.department_name ??
            value.speciality ??
            value.specialty ??
            value.specialization
        );
        return;
      }
      const normalized = String(value).trim();
      if (normalized) names.add(normalized);
    };

    addName(doctor?.departmentName);
    addName(doctor?.department_name);
    addName(doctor?.speciality);
    addName(doctor?.specialty);
    addName(doctor?.specialization);
    addName(doctor?.departmentNames);
    addName(doctor?.departments);

    collectDoctorDepartmentIds(doctor).forEach((departmentId) => {
      const department = allDepartments.find(
        (item) => String(item?.id) === String(departmentId)
      );
      if (department) addName(getDepartmentName(department));
    });

    return [...names];
  };

  const doctorMatchesDepartment = (doctor, departmentId) => {
    if (!departmentId) return true;
    const wanted = String(departmentId);
    const doctorIds = collectDoctorDepartmentIds(doctor).map(String);
    if (doctorIds.includes(wanted)) return true;
    const selected = allDepartments.find(
      (department) => String(department.id) === wanted
    );
    if (!selected) return false;
    const deptName = String(getDepartmentName(selected)).trim().toLowerCase();
    if (!deptName) return false;
    return getDoctorDepartmentNames(doctor).some(
      (name) => name.trim().toLowerCase() === deptName
    );
  };

  const doctorMatchesSearch = (doctor) => {
    if (!searchValue) return true;
    const doctorName = String(getDoctorName(doctor)).toLowerCase();
    if (doctorName.includes(searchValue)) return true;
    return getDoctorDepartmentNames(doctor).some((departmentName) =>
      departmentName.toLowerCase().includes(searchValue)
    );
  };

  const filteredDepartments = useMemo(() => {
    if (!searchValue) return allDepartments;
    return allDepartments.filter((department) => {
      const departmentName = String(getDepartmentName(department)).toLowerCase();
      if (departmentName.includes(searchValue)) return true;
      return doctorsInHospital.some((doctor) => {
        const doctorName = String(getDoctorName(doctor)).toLowerCase();
        if (!doctorName.includes(searchValue)) return false;
        const doctorIds = collectDoctorDepartmentIds(doctor).map(String);
        if (doctorIds.includes(String(department.id))) return true;
        return getDoctorDepartmentNames(doctor).some(
          (name) => name.toLowerCase() === departmentName
        );
      });
    });
  }, [allDepartments, searchValue, doctorsInHospital]);

  const filteredDoctors = useMemo(() => {
    return doctorsInHospital.filter((doctor) => {
      if (!doctorMatchesDepartment(doctor, selectedDepartment)) return false;
      return doctorMatchesSearch(doctor);
    });
  }, [doctorsInHospital, selectedDepartment, searchValue, allDepartments]);

  const doctorChoices = useMemo(() => {
    if (!lockedDoctor) return filteredDoctors;
    const lockedId = String(getDoctorId(lockedDoctor));
    if (filteredDoctors.some((doctor) => String(getDoctorId(doctor)) === lockedId)) {
      return filteredDoctors;
    }
    return [lockedDoctor, ...filteredDoctors];
  }, [filteredDoctors, lockedDoctor]);

  useEffect(() => {
    if (!lockedDoctor) return;
    const wantedDepartmentId =
      lockedDoctor.departmentId ?? lockedDoctor.department_id ?? null;
    if (wantedDepartmentId != null && wantedDepartmentId !== "") {
      const department = allDepartments.find(
        (item) => String(item.id) === String(wantedDepartmentId)
      );
      if (
        department &&
        (!selectedDepartment ||
          (String(selectedDepartment) === String(department.id) &&
            selectedDepartment !== department.id))
      ) {
        setSelectedDepartment(department.id);
      }
    }
    const doctor = doctorChoices.find(
      (item) => String(getDoctorId(item)) === String(getDoctorId(lockedDoctor))
    );
    if (!doctor) return;
    const doctorId = getDoctorId(doctor);
    if (
      !selectedDoctor ||
      (String(selectedDoctor) === String(doctorId) && selectedDoctor !== doctorId)
    ) {
      setSelectedDoctor(doctorId);
    }
  }, [lockedDoctor, allDepartments, doctorChoices, selectedDepartment, selectedDoctor]);

  const searchHasValue = Boolean(searchValue);
  const searchHasResults =
    !searchHasValue || filteredDoctors.length > 0 || filteredDepartments.length > 0;

  useEffect(() => {
    if (loadingDepartments || allDepartments.length === 0) return;
    if (
      selectedDepartment &&
      !filteredDepartments.some(
        (department) => String(department.id) === String(selectedDepartment)
      )
    ) {
      setSelectedDepartment("");
    }
  }, [doctorSearch, filteredDepartments, selectedDepartment, loadingDepartments, allDepartments.length]);

  useEffect(() => {
    if (
      selectedDoctor &&
      !doctorChoices.some(
        (doctor) => String(getDoctorId(doctor)) === String(selectedDoctor)
      )
    ) {
      setSelectedDoctor("");
    }
  }, [selectedDepartment, doctorSearch, doctorChoices, selectedDoctor]);

  useEffect(() => {
    if (!requirePrescription) return undefined;
    const id = String(prescriptionId || "").trim();
    if (!id || !patientId) {
      setPrescribedTestOptions([]);
      setSelectedPrescribedTests([]);
      setLockedDoctor(null);
      return undefined;
    }

    const lookupId = prescriptionLookupRef.current + 1;
    prescriptionLookupRef.current = lookupId;
    const timer = setTimeout(async () => {
      setVerifyingPrescription(true);
      try {
        const prescription = await getPrescriptionById(id);
        if (prescriptionLookupRef.current !== lookupId) return;
        const check = validatePrescriptionForLabBooking(prescription, patientId);
        if (!check.ok) {
          setPrescribedTestOptions(check.prescribedTests || []);
          setSelectedPrescribedTests([]);
          setLockedDoctor(null);
          toast.error(t(check.message, check.messageVars));
          return;
        }

        const doctorId =
          prescription?.doctorId ??
          prescription?.doctor_id ??
          prescription?.doctor?.id ??
          null;
        let doctor = null;
        if (doctorId) {
          try {
            doctor = await getDoctorById(doctorId);
          } catch (doctorError) {
            console.warn("Unable to load the prescribing doctor:", doctorError);
          }
        }
        if (prescriptionLookupRef.current !== lookupId) return;

        const departmentId =
          doctor?.departmentId ??
          doctor?.department_id ??
          prescription?.departmentId ??
          prescription?.department_id ??
          "";
        if (departmentId) setSelectedDepartment(String(departmentId));
        if (doctor && getDoctorId(doctor)) {
          const normalizedDoctor = {
            ...doctor,
            id: getDoctorId(doctor),
            departmentIds: collectDoctorDepartmentIds(doctor),
          };
          setLockedDoctor(normalizedDoctor);
          setSelectedDoctor(String(getDoctorId(normalizedDoctor)));
        }

        const options = check.prescribedTests?.length ? check.prescribedTests : check.tests;
        setPrescribedTestOptions(options);
        setSelectedPrescribedTests((check.tests || []).filter((test) => test.bookable !== false));
      } catch (error) {
        if (prescriptionLookupRef.current !== lookupId) return;
        console.error("Error verifying prescription:", error);
        setPrescribedTestOptions([]);
        setSelectedPrescribedTests([]);
        setLockedDoctor(null);
      } finally {
        if (prescriptionLookupRef.current === lookupId) setVerifyingPrescription(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [requirePrescription, prescriptionId, patientId]);

  const goToDoctor = (doctorId, extraQuery = "") => {
    if (!doctorId) return;
    const type = appointmentType === "virtual" ? "virtual" : "in-person";
    navigate(`/patient/doctor/${doctorId}?type=${type}${extraQuery}`);
  };

  const handleBook = async () => {
    if (!selectedDoctor) {
      toast.info(t("Search and select a doctor or speciality first."));
      return;
    }
    const doctor = mergeDoctorsById([
      ...doctors,
      ...departmentDoctors,
      ...(lockedDoctor ? [lockedDoctor] : []),
    ]).find((item) => String(getDoctorId(item)) === String(selectedDoctor));
    if (!doctor) {
      toast.error(t("Selected doctor could not be found. Please search again."));
      return;
    }

    let prescriptionQuery = "";
    if (requirePrescription) {
      const id = String(prescriptionId || "").trim();
      if (!id) {
        toast.error(t("Please enter a prescription ID."));
        return;
      }
      if (!patientId) {
        toast.error(t("Patient information not found. Please contact support."));
        return;
      }
      setVerifyingPrescription(true);
      try {
        const prescription = await getPrescriptionById(id);
        const check = validatePrescriptionForLabBooking(prescription, patientId);
        if (!check.ok) {
          toast.error(t(check.message, check.messageVars));
          return;
        }
        const allowedIds = new Set(
          (check.tests || [])
            .filter((test) => test.bookable !== false)
            .map((test) => Number(test.labTestId))
        );
        const chosenIds = selectedPrescribedTests
          .filter((test) => test.bookable !== false)
          .map((test) => Number(test.labTestId ?? test.id))
          .filter((id) => allowedIds.has(id));
        if (!chosenIds.length) {
          toast.error(t("Please select at least one prescribed test."));
          return;
        }
        prescriptionQuery = `&prescriptionId=${encodeURIComponent(check.prescriptionId)}&labTestIds=${encodeURIComponent(chosenIds.join(","))}`;
      } catch (error) {
        console.error("Error verifying prescription:", error);
        toast.error(
          t(
            error?.response?.data?.message ||
              "Prescription ID is invalid or could not be verified."
          )
        );
        return;
      } finally {
        setVerifyingPrescription(false);
      }
    }

    goToDoctor(selectedDoctor, prescriptionQuery);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "flex-start",
      }}
    >
      {requirePrescription && (
        <TextField
          label={t("Prescription ID")}
          value={prescriptionId}
          onChange={(e) => setPrescriptionId(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleBook();
          }}
          inputProps={{ inputMode: "numeric" }}
          size="small"
          sx={SEARCH_FIELD_SX}
        />
      )}
      {requirePrescription && prescribedTestOptions.length > 0 && (
        <Autocomplete
          multiple
          size="small"
          options={prescribedTestOptions}
          value={selectedPrescribedTests}
          onChange={(event, newValue) =>
            setSelectedPrescribedTests((newValue || []).filter((test) => test.bookable !== false))
          }
          getOptionLabel={(test) => {
            const name = test?.testName || test?.name || `Lab Test #${test?.labTestId ?? test?.id}`;
            if (test?.bookable !== false) return name;
            if (test?.bookingState === "completed") {
              return `${name} (${t("Completed — booking not required")})`;
            }
            if (test?.bookingState === "rejected") {
              return `${name} (${t("Rejected")})`;
            }
            return `${name} (${t("Already in progress")})`;
          }}
          getOptionDisabled={(test) => test?.bookable === false}
          isOptionEqualToValue={(option, value) =>
            String(option?.labTestId ?? option?.id) === String(value?.labTestId ?? value?.id)
          }
          sx={{
            flex: "1 1 240px",
            minWidth: { xs: "100%", sm: 200 },
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#fff",
              fontSize: "0.875rem",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t("Tests prescribed by doctor")}
              placeholder={t("Select prescribed test(s)")}
            />
          )}
        />
      )}
      <TextField
        select
        label={t("Department")}
        value={selectedDepartment}
        onChange={(e) => {
          setSelectedDepartment(e.target.value);
          setSelectedDoctor("");
        }}
        disabled={loadingDepartments}
        size="small"
        sx={SEARCH_FIELD_SX}
      >
        <MenuItem value="">{t("All departments")}</MenuItem>
        {filteredDepartments.map((department) => (
          <MenuItem key={department.id} value={department.id}>
            {getDepartmentName(department)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label={t("Available Doctor")}
        value={selectedDoctor}
        onChange={(e) => {
          const doctorId = e.target.value;
          setSelectedDoctor(doctorId);
        }}
        disabled={loadingDoctors}
        size="small"
        sx={SEARCH_FIELD_SX}
      >
        <MenuItem value="">
          {selectedDepartment && filteredDoctors.length === 0
            ? t("No doctor available for this department")
            : filteredDoctors.length === 0
              ? t("No matching doctors")
              : t("Select doctor")}
        </MenuItem>
        {doctorChoices.map((doctor) => {
          const doctorId = getDoctorId(doctor);
          return (
            <MenuItem key={doctorId} value={doctorId}>
              {getDoctorName(doctor)}
            </MenuItem>
          );
        })}
      </TextField>
      <Box
        sx={{
          ...SEARCH_FIELD_SX,
          flex: "1 1 240px",
          position: "relative",
        }}
      >
        <TextField
          fullWidth
          size="small"
          label={t("Search Doctor or Department")}
          value={doctorSearch}
          onChange={(e) => {
            setDoctorSearch(e.target.value);
            setSelectedDoctor("");
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: TEXT_GREY }} />
              </InputAdornment>
            ),
          }}
        />
        {searchHasValue && (
          <Paper
            elevation={4}
            sx={{
              position: "absolute",
              top: 44,
              left: 0,
              right: 0,
              zIndex: 20,
              maxHeight: 320,
              overflowY: "auto",
              border: `1px solid ${BORDER}`,
              borderRadius: "10px",
              backgroundColor: "#fff",
            }}
          >
            {!searchHasResults ? (
              <Typography
                sx={{
                  px: 2,
                  py: 2,
                  fontSize: 14,
                  color: TEXT_GREY,
                  textAlign: "center",
                }}
              >
                {t("No results found")}
              </Typography>
            ) : (
              <>
                {filteredDepartments.length > 0 && (
                  <Box sx={{ p: 1 }}>
                    <Typography
                      sx={{
                        px: 1,
                        py: 0.6,
                        fontSize: 11,
                        fontWeight: 800,
                        color: TEXT_GREY,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {t("Departments")}
                    </Typography>
                    {filteredDepartments.slice(0, 8).map((department) => (
                      <Box
                        key={`search-department-${department.id}`}
                        onClick={() => {
                          setSelectedDepartment(String(department.id));
                          setSelectedDoctor("");
                          setDoctorSearch(getDepartmentName(department));
                        }}
                        sx={{
                          px: 1,
                          py: 1,
                          borderRadius: "7px",
                          cursor: "pointer",
                          "&:hover": { backgroundColor: BLUE_LIGHT },
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                          {getDepartmentName(department)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {filteredDoctors.length > 0 && (
                  <Box
                    sx={{
                      p: 1,
                      borderTop: filteredDepartments.length ? `1px solid ${BORDER}` : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        px: 1,
                        py: 0.6,
                        fontSize: 11,
                        fontWeight: 800,
                        color: TEXT_GREY,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {t("Doctors")}
                    </Typography>
                    {filteredDoctors.slice(0, 10).map((doctor) => {
                      const doctorId = getDoctorId(doctor);
                      const departmentNames = getDoctorDepartmentNames(doctor);
                      return (
                        <Box
                          key={`search-doctor-${doctorId}`}
                          onClick={() => {
                            setSelectedDoctor(String(doctorId));
                            setDoctorSearch(getDoctorName(doctor));
                          }}
                          sx={{
                            px: 1,
                            py: 1,
                            borderRadius: "7px",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: BLUE_LIGHT },
                          }}
                        >
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {getDoctorName(doctor)}
                          </Typography>
                          {departmentNames.length > 0 && (
                            <Typography sx={{ fontSize: 11.5, color: TEXT_GREY }}>
                              {departmentNames.join(", ")}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>
      <Button
        variant="contained"
        onClick={handleBook}
        disabled={verifyingPrescription}
        sx={{
          height: 40,
          minHeight: 40,
          px: 3,
          flex: { xs: "1 1 100%", sm: "0 0 120px" },
          backgroundColor: BLUE,
          boxShadow: "none",
          fontWeight: 700,
          textTransform: "none",
          "&:hover": { backgroundColor: BLUE_DARK, boxShadow: "none" },
        }}
      >
        {verifyingPrescription ? <CircularProgress size={22} color="inherit" /> : t("Book")}
      </Button>
    </Box>
  );
};

export default PatientDoctorSearchPanel;
